<template>
    <div class="header-container">
        <div id="acmsHeader" class="bg-white shadow overflow-hidden sm:rounded-lg mb-5">
            <div class="grid grid-cols-1 px-4 py-5 sm:px-6 place-items-center">
                <div class="col-span-1 justify-self-start">
                    <div class="align-middle">
                        <h3 class="text-lg leading-6 font-medium text-gray-900">Asbestos Containing Materials ({{ acms.length }})</h3>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div id="acmsContainer">
        <template v-for="(acm, $i) of acms">
            <div class="bg-white shadow overflow-hidden sm:rounded-lg mb-1 sm:mb-4">

                <div class="grid grid-cols-2 gap-5">

                    <div class="col-span-2 border-t border-gray-200">
                        <dl>
                            <div class="border-b px-4 py-3 grid grid-cols-1">
                                <image-container @image-click="handleImageClick" :item="acm" />
                            </div>

                            <div class="border-b px-1 pt-3 pb-1">
                                <div v-if="acm.acm_type" class="py-1 px-3 text-sm block align-baseline font-bold rounded">
                                    ACM Type:
                                    <span class="mr-3 text-sm inline-block leading-none text-center align-baseline font-bold rounded">
                                    {{ acm.acm_type ?? 'Unknown' }}
                                    </span>
                                </div>
                                <div v-if="acm.fibre_types.length" class="py-1 px-3 text-sm block align-baseline font-bold rounded">
                                    Fibre Types:
                                    <template v-for="fibreType of acm.fibre_types">
                                        <span :class="fibreType.colour + ' inline-block mr-3 text-sm py-1 px-3 leading-none text-center whitespace-nowrap align-baseline font-bold rounded'">{{ fibreType.label }}</span>
                                    </template>
                                </div>
                                <div v-if="acm.risk_rating?.label" class="py-1 px-3 text-sm block align-baseline font-bold rounded">
                                    Risk Rating:
                                    <span :class="acm.risk_rating.colour + ' mr-3 text-sm inline-block py-1 px-3 leading-none text-center whitespace-nowrap align-baseline font-bold rounded'">
                                    {{ acm.risk_rating.label }}
                                    </span>
                                </div>
                                <div v-if="acm.condition?.label" class="py-1 px-3 text-sm block align-baseline font-bold rounded">
                                    Condition:
                                    <span :class="acm.condition.colour + ' mr-3 text-sm inline-block py-1 px-3 leading-none text-center whitespace-nowrap align-baseline font-bold rounded'">
                                    {{ acm.condition.label }}
                                    </span>
                                </div>
                                <div v-if="acm.status" class="py-1 px-3 text-sm block align-baseline font-bold rounded">
                                    Status:
                                    <span class="mr-3 text-sm inline-block leading-none text-center whitespace-nowrap align-baseline font-bold rounded">
                                    {{ acm.status }}
                                    </span>
                                </div>
                            </div>

                            <div class="border-b px-4 py-3 grid grid-cols-2">
                                <div class="col-span-2 sm:col-span-1 font-bold">
                                    <dt class="text-sm text-gray-500">
                                        Location:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.location }}, {{ acm.room }}
                                    </dd>
                                </div>
                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Building:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.building }}
                                    </dd>
                                </div>
                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Floor:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.floor }}
                                    </dd>
                                </div>

                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Reference No.:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.reference }}
                                    </dd>
                                </div>

                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Total RA Score:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.total_ra_score }}
                                    </dd>
                                </div>
                            </div>

                            <div :class="['border-b px-4 py-3 grid grid-cols-2 sm:block', { 'hidden': !isExpanded($i) } ]">
                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Sample No.:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.sample_number }}
                                    </dd>
                                </div>
                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Surface Treatment:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.surface_treatment }}
                                    </dd>
                                </div>
                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Amount:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.amount }}
                                    </dd>
                                </div>
                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        (Unit) Amount:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.amount_unit ?? 'Unknown' }}
                                    </dd>
                                </div>
                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Material Risk Assessment:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.material_risk_assessment }}
                                    </dd>
                                </div>
                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Priority Risk Assessment:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.priority_risk_assessment }}
                                    </dd>
                                </div>
                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Recommendation:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.recommendation }}
                                    </dd>
                                </div>
                                <div class="col-span-2 sm:col-span-1">
                                    <dt class="text-sm font-medium text-gray-500">
                                        Action / Review Date:
                                    </dt>
                                    <dd class="text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {{ acm.action_date }}
                                    </dd>
                                </div>
                            </div>
                        </dl>

                    </div>

                </div>
            </div>
            <button @click="toggleExpand($i)" class="block sm:hidden mb-4 w-full inline-flex justify-center py-2 px-4 border border-uknar-primary-dark shadow-sm text-sm font-medium rounded-md text-white bg-uknar-primary hover:bg-uknar-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-uknar-primary">
                <template v-if="!isExpanded($i)">View Additional Information</template>
                <template v-else>View Less Information</template>
            </button>
        </template>
    </div>
    <div v-if="!acms.length" class="overflow-hidden shadow rounded-lg mb-4">
        <div class="bg-gray-50 px-4 py-5 sm:p-6 text-center">
            <p>There are no Asbestos Containing Materials identified.</p>
        </div>
    </div>
</template>

<script>
import ImageContainer from "./ImageContainer";
export default {
    name: 'Acms',
    components: { ImageContainer },
    props: {
        acms: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            expanded: []
        }
    },

    methods: {
        isExpanded(i) {
            return this.expanded.indexOf(i) > -1;
        },

        toggleExpand(i) {
            if(this.isExpanded(i)) {
                this.expanded.splice(this.expanded.indexOf(i), 1);
            } else {
                this.expanded.push(i);
            }
        },

        handleImageClick(imageUrl) {
            this.$emit('image-click', imageUrl);
        }
    }
}
</script>

<style scoped>
dt, dd {
    display: inline-block;
    margin-right: 6px;
}

#acmsHeader {
    margin-top: 0px;
    left: 0;
    z-index: 1;
}
</style>
